import React, { useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Outlet, useLocation } from 'react-router-dom'; // Import useLocation

export const RequiredAuth: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation(); // Get current location
  if (process.env.NODE_ENV === 'development') {
    console.log(`RequiredAuth - Render. Path: ${location.pathname}, authState:`, authState);
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`RequiredAuth - useEffect running. Path: ${location.pathname}, authState:`, authState);
    }
    if (!authState) {
      console.log("RequiredAuth - authState is null/undefined, waiting...");
      return; // Wait for SDK to initialize authState
    }

    // Only initiate redirect if not authenticated, not pending, AND not on the callback route itself
    if (!authState.isAuthenticated && !authState.isPending && location.pathname !== '/authorization-code/callback') {
      console.log("RequiredAuth - Not authenticated, not pending, not on callback. Attempting redirect...");
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect().catch(err => {
          console.error("signInWithRedirect failed:", err);
      });
    } else {
      console.log(`RequiredAuth - State: Authenticated=${authState.isAuthenticated}, Pending=${authState.isPending}, Path=${location.pathname}`);
      console.log("Authenticated! Token claims:", authState.idToken?.claims);
    }
    // Add location.pathname to dependency array
  }, [oktaAuth, authState, location.pathname]);

  // If authState is not yet determined, render nothing - let Security handle loading
  if (!authState) {
     console.log("RequiredAuth - Rendering loading indicator (authState null).");
     return <div>Loading...</div>;
  }

  // If authenticated, render the protected content
  if (authState.isAuthenticated) {
      console.log("RequiredAuth - Rendering Outlet.");
      return <Outlet />;
  }

  // If on the callback route OR in pending state, render nothing - let Security handle loading
  if (authState.isPending || location.pathname === '/authorization-code/callback') {
      console.log("RequiredAuth - Rendering null (pending or on callback path).");
      // The <Security> component should be handling the UI during this phase
      return null; // Let Security's default loading/processing occur
  }

  // Fallback case (should ideally not be reached if redirect logic is sound)
  // Render a minimal loading indicator or null while waiting for redirect
  if (process.env.NODE_ENV === 'development') {
    console.log("RequiredAuth - Rendering null (fallback state - likely pre-redirect).");
  }
  return null;
};